import { useState, type FormEvent } from 'react'
import { trackerApi, ApiError } from '../api/client'
import { writeToken } from '../lib/screen-token'

/**
 * Окно запроса авторизации: экран не привязан либо его доступ отозван.
 *
 * Показывается всегда, когда токена нет или он больше не действует — по требованию
 * «если экран не авторизован и мы не получили его айди, должно висеть окно запроса
 * авторизации». Код многоразовый, поэтому перепривязка после чистки кеша киоска
 * работает без участия кассира.
 */
export function PairPage({
  reason,
  onPaired
}: {
  /** Почему просим код: пусто — экран просто не привязан. */
  reason?: string | null
  onPaired: () => void
}): React.JSX.Element {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  /** Показываем код как XXXX-XXXX, а отправляем без дефиса. */
  const pretty = (raw: string): string => {
    const clean = raw
      .toUpperCase()
      .replace(/[^0-9A-Z]/g, '')
      .slice(0, 8)
    return clean.length > 4 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean
  }

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const clean = code.replace(/[^0-9A-Z]/gi, '')
    if (clean.length < 6 || busy) return
    // Самая частая ошибка: несут 6-значный код локальной сети. Отвечаем сразу и
    // по делу, не тратя попытку из лимита на сервере.
    if (/^\d{6}$/.test(clean)) {
      setErr(
        'Это 6-значный код для планшета в одной сети с кассой. ' +
          'Для интернета нужен код облачного экрана — вида XXXX-XXXX, буквы и цифры.'
      )
      return
    }
    setBusy(true)
    setErr(null)
    try {
      const res = await trackerApi.pair(clean)
      writeToken(res.token)
      onPaired()
    } catch (e2) {
      const status = e2 instanceof ApiError ? e2.status : 0
      setErr(
        status === 429
          ? 'Слишком много попыток. Подождите минуту.'
          : ((e2 as Error).message ?? 'Не удалось подключить экран')
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="pair">
      <h2>Экран не подключён</h2>
      {reason && <p className="err">{reason}</p>}
      <form onSubmit={(e) => void submit(e)}>
        <input
          value={code}
          onChange={(e) => setCode(pretty(e.target.value))}
          placeholder="XXXX-XXXX"
          autoFocus
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          inputMode="text"
          aria-label="Код привязки экрана"
        />
        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Подключаем…' : 'Подключить'}
        </button>
        {err && <p className="err">{err}</p>}
      </form>
      {/* Бэкофис назван ПЕРВЫМ: колонку «Код через интернет» он показывает уже
          сейчас, а касса — только с обновлённой сборкой. Клуб на старой версии
          искал код на кассе, не находил и нёс 6-значный код локальной сети. */}
      <p>
        Код смотрите в бэкофисе: «Устройства» → колонка <b>«Код через интернет»</b>.
        <br />
        На кассе он там же — «Устройства» → нужный экран, если касса обновлена.
        <br />
        Это 8 символов с буквами (вида XXXX-XXXX), а не 6-значный код локальной сети.
      </p>
    </div>
  )
}
